import { useState } from "react";
import { useForm } from "react-hook-form";
import { Loader } from "rsuite";

import Input from "../Inputs/Input";
import API from "../../utils/api";
import { useModal } from "../../context/modal-context";
import { useConfig } from "../../context/config-context";

/**
 * UpdateMobilePhoneModal - Modale pour modifier le numéro de téléphone mobile
 *
 * Fonctionnalités:
 * - Affichage du numéro actuel (ou "- -" si vide)
 * - Validation du nouveau numéro (format français)
 * - Appel API PUT /api/prescriber/profile
 * - Rechargement de la page après succès
 */
export default function UpdateMobilePhoneModal({ currentPhone, onSuccess }) {
  const { hideModal } = useModal();
  const siteConfig = useConfig();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const phoneValue = watch("newMobilePhone", "");
  const cleaned = (phoneValue || "").replace(/[\s.-]/g, "");
  const isValidPhone =
    /^(?:\+33|0033|0)[67]\d{8}$/.test(cleaned) ||
    /^(?:\+262|\+590|\+594|\+596)\d{9}$/.test(cleaned);

  /**
   * Validation du numéro de téléphone français
   */
  const validatePhoneNumber = (value) => {
    if (!value || value.trim() === "") {
      return "Le numéro de téléphone est requis";
    }

    // Nettoyer le numéro (enlever espaces, points, tirets)
    const cleaned = value.replace(/[\s.-]/g, "");

    // Formats acceptés:
    // - 06/07 + 8 chiffres (mobile métropole)
    // - +262/+590/+594/+596 + 9 chiffres (DOM-TOM)
    const frenchMobileRegex = /^(?:\+33|0033|0)[67]\d{8}$/;
    const domTomRegex = /^(?:\+262|\+590|\+594|\+596)\d{9}$/;

    const isFrenchMobile = frenchMobileRegex.test(cleaned);
    const isDomTomMobile = domTomRegex.test(cleaned);

    if (!isFrenchMobile && !isDomTomMobile) {
      return "Numéro de téléphone invalide. Format attendu : 06/07 ou +262/+590/+594/+596";
    }

    return true;
  };

  /**
   * Soumet le nouveau numéro de téléphone
   */
  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const response = await API.put("/prescriber/profile", {
        mobilePhone: data.newMobilePhone,
      });

      if (response.data.success) {
        hideModal();
        // Appeler le callback de succès (rafraîchir les données utilisateur)
        if (onSuccess) {
          onSuccess(data.newMobilePhone);
        }
      }
    } catch (error) {
      // Les erreurs sont gérées par l'interceptor axios (toast)
      console.error("Erreur mise à jour téléphone:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-w-[500px]">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 pt-[22px]">
        <div className="flex items-center justify-center flex-shrink-0">
          <img
            src={siteConfig.medias.phone_svg.path}
            alt={siteConfig.medias.phone_svg.alt}
            width={27}
            height={26}
          />
        </div>
        <h2 className="text-lg font-semibold text-neutral-dark border-b border-neutral-lighter flex-1">
          Modifier le numéro de téléphone mobile
        </h2>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4 px-6 py-4"
      >
        {/* Numéro actuel */}
        <div className="flex flex-col space-y-1">
          <label className="text-base font-semibold text-neutral-dark">
            Téléphone mobile
          </label>
          <p className="text-base text-neutral-dark">{currentPhone || "- -"}</p>
        </div>

        {/* Nouveau numéro */}
        <div>
          <label className="text-sm font-semibold text-neutral-dark">
            Nouveau n° de téléphone mobile
          </label>
          <Input
            {...register("newMobilePhone", {
              required: "Le numéro de téléphone est requis",
              validate: validatePhoneNumber,
            })}
            id="newMobilePhone"
            placeholder="XXXXXXXXXX"
            className="!w-fit min-w-[262px]"
            type="text"
            error={errors.newMobilePhone}
          />
        </div>

        {/* Séparateur + Boutons */}
        <div className="border-t border-gray-200 pt-4 flex justify-between gap-3">
          <button
            type="button"
            onClick={hideModal}
            className="px-[12.5px] py-1.5 border border-primary text-primary rounded hover:bg-primary/5 transition-colors font-semibold"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading || !isValidPhone}
            className="px-[12.5px] py-1.5 bg-primary text-white rounded hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold"
          >
            {loading ? <Loader /> : "Valider"}
          </button>
        </div>
      </form>
    </div>
  );
}
